import React from 'react';
import PropTypes from 'prop-types';
import { makeStyles } from '@material-ui/core/styles';
import cn from 'classnames';
import { Field, Form, Formik } from 'formik';
import { get } from 'lodash';
import { Grid } from '@material-ui/core';
import { object, string } from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../src/i18n';
import SubmitModalLayout from '../SubmitModalLayout';
import { TP, urlRegex } from '../../constants/consts';
import CountryCityBlock from '../CountryCityBlock';
import EditModeCard from '../EditModeCard';
import FormikTextField from '../Fields/TextField';
import TextArea from '../Fields/TextArea';
import FormikPhoneField from '../Fields/FormikPhoneField';
import { createEntity, useBufferValueAction } from '../../containers/App/actions';
import { COMPANIES } from '../../constants/endpointConstants';
import { selectBufferValueData } from '../../containers/App/selectors';

const useStyles = makeStyles(theme => ({
  form: {
    margin: theme.spacing(2),
  },
  fieldsBox: {
    padding: theme.spacing(2),
  },
}));

const companyFields = {
  name: {
    name: 'name',
    label: `${TP}.SORT_COMPANY_NAME`,
  },
  url: {
    name: 'website',
    label: `${TP}.FN_COMPANY_WEBSITE`,
  },
  country: {
    name: 'country',
  },
  city: {
    name: 'city',
  },
  address: {
    name: 'address',
    label: `${TP}.m_ADDRESS`,
  },
  email: {
    name: 'email',
    label: `${TP}.FN_COMPANY_EMAIL`,
  },
  phone: {
    name: 'phone',
    label: `${TP}.m_REGISTRATION_PHONENUMBER`,
  },
};

const { name, url, country, city, address, email, phone } = companyFields;

const initialValues = companyName => ({
  [name.name]: companyName,
  [url.name]: '',
  [country.name]: null,
  [city.name]: null,
  [address.name]: '',
  [email.name]: '',
  [phone.name]: '',
});

const AddNewCompanyModal = ({ className, onClose, companyName }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const formRef = React.createRef(null);
  const loadingBufferValue = useSelector(selectBufferValueData());

  const createCompany = values => {
    const phoneNumber = get(values, phone.name, '');
    const getIdFromValue = field => (get(values, `${field}.value`) ? { id: get(values, `${field}.value`) } : null);
    dispatch(
      createEntity({
        endpoint: COMPANIES,
        sagaRoutine: useBufferValueAction,
        params: {
          name: get(values, name.name),
          url: get(values, url.name),
          country: getIdFromValue(country.name),
          city: getIdFromValue(city.name),
          address: get(values, address.name),
          email: get(values, email.name),
          phones: phoneNumber?.length > 0 ? [phoneNumber] : [],
        },
        callback: company => onClose(company),
      }),
    );
  };

  return (
    <SubmitModalLayout
      className={cn(classes.root, className)}
      isOpened
      onClose={onClose}
      title1={t(`${TP}.FN_ADD_NEW_COMPANY`)}
      submitFunc={() => {
        get(formRef, 'current.submitForm', () => {})();
      }}
      isLoading={loadingBufferValue?.loading}
    >
      <EditModeCard className={classes.form}>
        <Formik
          initialValues={initialValues(companyName)}
          onSubmit={createCompany}
          validationSchema={AddNewCompanySchema}
          innerRef={formRef}
        >
          {({ values, setFieldValue }) => (
            <Form>
              <Grid container spacing={2} className={classes.fieldsBox} alignItems="flex-start">
                <Grid item sm={6} xs={12}>
                  <Field name={name.name} component={FormikTextField} label={t(name.label)} />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field name={url.name} component={FormikTextField} label={t(url.label)} />
                </Grid>
                <CountryCityBlock formValues={values} setFieldValue={setFieldValue} isCreatable />
                <Grid item sm={12} xs={12}>
                  <Field name={address.name} component={TextArea} label={t(address.label)} />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field name={email.name} component={FormikTextField} label={t(email.label)} />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <Field name={phone.name} label={t(phone.label)} component={FormikPhoneField} />
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </EditModeCard>
    </SubmitModalLayout>
  );
};

AddNewCompanyModal.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  companyName: PropTypes.string,
};

AddNewCompanyModal.defaultProps = {
  companyName: '',
};

export default AddNewCompanyModal;

const AddNewCompanySchema = () =>
  object().shape({
    [name.name]: string()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [url.name]: string()
      .matches(urlRegex, `${TP}.FN_WEB_LINK_INVALID`)
      .nullable(),
    [email.name]: string()
      .nullable()
      .email(`${TP}.m_MAILNONVALID`),
    [country.name]: object()
      .nullable()
      .required(`${TP}.FN_REQUIRED_FIELD`),
    [city.name]: object().nullable(),
    [address.name]: string().nullable(),
    [phone.name]: string().nullable(),
  });
